import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { IncorporationRequestStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import { omitLegacyOwnershipFlags } from '../../common/helpers';
import {
  BoardPositionEnum,
  findDirectorByRole,
  getDirectorName,
  groupTitlesByDirectorIds,
} from '../../components/Director/common';
import { RolesSchemaFields, rolesSchema } from '../../schema/base/roles';
import FormInputWrapper from './FormInputWrapper';

const Roles = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification: TSetNotification;
  token: string;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const presidentDirector = findDirectorByRole(
    data,
    BoardPositionEnum.President
  );
  const secretaryDirector = findDirectorByRole(
    data,
    BoardPositionEnum.Secretary
  );
  const treasurerDirector = findDirectorByRole(
    data,
    BoardPositionEnum.Treasurer
  );

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
  } = useForm<RolesSchemaFields>({
    resolver: zodResolver(rolesSchema),
    defaultValues: {
      President: presidentDirector?.id?.toString(),
      Secretary: secretaryDirector?.id?.toString(),
      Treasurer: treasurerDirector?.id?.toString(),
    },
  });

  const selectMenuOptions =
    data?.directors
      ?.filter((director) =>
        director.corporationHasDirector?.some(
          (directorWithCorporation) =>
            directorWithCorporation.corporationId === data.corporationId
        )
      )
      ?.map((eachDirector) => ({
        text: getDirectorName(eachDirector),
        value: eachDirector.id.toString(),
      })) || [];

  const onSubmit = async (formData: RolesSchemaFields) => {
    const directorIdsVsTitles = groupTitlesByDirectorIds(formData);

    const directorsBody = [];

    Object.entries(directorIdsVsTitles).forEach(
      ([directorId, directorTitles]) => {
        const director = data?.directors?.find((dir) => dir.id === +directorId);
        const corporationHasDirector = director?.corporationHasDirector?.find(
          ({ corporationId }) =>
            corporationId === data.corporationId && +directorId
        );

        const additionalDirectorInformation = {
          ...omitLegacyOwnershipFlags(
            corporationHasDirector?.additionalDirectorInformation
          ),
          boardPosition: (directorTitles as BoardPositionEnum[]).map(
            (each) => BoardPositionEnum[each]
          ),
        };

        directorsBody.push({
          id: director.id,
          corporationHasDirector: [
            {
              ...corporationHasDirector,
              additionalDirectorInformation,
            },
          ],
        });
      }
    );

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          directors: directorsBody,
          status: IncorporationRequestStatus.InProgress,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      return;
    }

    await mutate();

    next();
  };

  return (
    <div className="flex flex-col gap-14 max-w-[500px]">
      <FormInputWrapper
        id="officers"
        text="Designate company officers"
        description="Appoint individuals to key roles required for company formation. You may assign multiple roles to a single individual."
        footnote={1}
      >
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="body-14-medium">President</div>
              <p className="body-14-regular">
                Responsible for guiding the company's strategic direction.
              </p>
            </div>
            <Select
              onValueChange={(value) => setValue('President', value)}
              value={getValues('President')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select President" />
              </SelectTrigger>
              <SelectContent>
                {selectMenuOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="body-14-medium">Secretary</div>
              <p className="body-14-regular">
                Oversees administrative duties, including records and
                communications management.
              </p>
            </div>
            <Select
              onValueChange={(value) => setValue('Secretary', value)}
              value={getValues('Secretary')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Secretary" />
              </SelectTrigger>
              <SelectContent>
                {selectMenuOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="body-14-medium">Treasurer</div>
              <p className="body-14-regular">
                Manages all financial aspects of the company.
              </p>
            </div>
            <Select
              onValueChange={(value) => setValue('Treasurer', value)}
              value={getValues('Treasurer')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Treasurer" />
              </SelectTrigger>
              <SelectContent>
                {selectMenuOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </FormInputWrapper>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          <span>Continue</span>
          <ProceedIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Roles;
